import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Eye, ImagePlus, Loader2, Save, Sparkles, Star, Trash2, X } from 'lucide-react';
import RichTextEditor from '../../components/RichTextEditor';
import SeoPreview from '../../components/SeoPreview';
import PersianDatePicker from '../../components/PersianDatePicker';
import { BrandSpinnerRing } from '../../components/SiteBrandedLoader';
import { articlesService, uploadsService, ArticleCategoryAdmin, ArticleTagUsage } from '../../services/api';
import { Article } from '../../types';
import BlogSectionNav from './BlogSectionNav';
import ConfirmDialog from './ConfirmDialog';
import {
  apiErrorMessage,
  articleStatus,
  clockToSeconds,
  CONTENT_SNIPPETS,
  faDateTime,
  faNumber,
  imagePreviewUrl,
  publicArticleUrl,
  secondsToClock,
  slugify,
  STATUS_META,
  blogSiteBaseUrl,
} from './blogAdmin';

type PublishMode = 'draft' | 'publish' | 'schedule';
type Tab = 'content' | 'media' | 'seo' | 'settings';

interface FormState {
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  cardSummary: string;
  kicker: string;
  featuredImage: string;
  featuredImageAlt: string;
  featuredImageCaption: string;
  categoryId: string;
  tags: string[];
  author: string;
  authorRole: string;
  authorBio: string;
  authorAvatar: string;
  videoUrl: string;
  videoPoster: string;
  videoDuration: string;
  videoCaption: string;
  videoQuality: string;
  quoteText: string;
  quoteAuthor: string;
  metaTitle: string;
  metaDescription: string;
  focusKeyword: string;
  metaKeywords: string[];
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  isFeatured: boolean;
  allowComments: boolean;
  readingTime: string;
  relatedArticles: string[];
  mode: PublishMode;
  scheduledAt: string;
}

const DEFAULT_AUTHOR = 'تحریریه من حقیقی';

const emptyForm: FormState = {
  title: '',
  slug: '',
  content: '',
  excerpt: '',
  cardSummary: '',
  kicker: '',
  featuredImage: '',
  featuredImageAlt: '',
  featuredImageCaption: '',
  categoryId: '',
  tags: [],
  author: DEFAULT_AUTHOR,
  authorRole: 'نویسندگان تخصصی روان‌شناسی',
  authorBio: '',
  authorAvatar: '',
  videoUrl: '',
  videoPoster: '',
  videoDuration: '',
  videoCaption: '',
  videoQuality: '',
  quoteText: '',
  quoteAuthor: '',
  metaTitle: '',
  metaDescription: '',
  focusKeyword: '',
  metaKeywords: [],
  canonicalUrl: '',
  ogTitle: '',
  ogDescription: '',
  ogImage: '',
  isFeatured: false,
  allowComments: true,
  readingTime: '',
  relatedArticles: [],
  mode: 'draft',
  scheduledAt: '',
};

const fromArticle = (a: Article): FormState => {
  const st = articleStatus(a);
  return {
    title: a.title || '',
    slug: a.slug || '',
    content: a.content || '',
    excerpt: a.excerpt || '',
    cardSummary: a.cardSummary || '',
    kicker: a.kicker || '',
    featuredImage: a.featuredImageKey || '',
    featuredImageAlt: a.featuredImageAlt || '',
    featuredImageCaption: a.featuredImageCaption || '',
    categoryId: a.categoryId || '',
    tags: a.tags || [],
    author: a.author || '',
    authorRole: a.authorRole || '',
    authorBio: a.authorBio || '',
    authorAvatar: a.authorAvatarKey || '',
    videoUrl: a.videoUrl || '',
    videoPoster: a.videoPosterKey || '',
    videoDuration: secondsToClock(a.videoDuration),
    videoCaption: a.videoCaption || '',
    videoQuality: a.videoQuality || '',
    quoteText: a.quoteText || '',
    quoteAuthor: a.quoteAuthor || '',
    metaTitle: a.metaTitle || '',
    metaDescription: a.metaDescription || '',
    focusKeyword: a.focusKeyword || '',
    metaKeywords: a.metaKeywords || [],
    canonicalUrl: a.canonicalUrl || '',
    ogTitle: a.ogTitle || '',
    ogDescription: a.ogDescription || '',
    ogImage: a.ogImage || '',
    isFeatured: !!a.isFeatured,
    allowComments: a.allowComments !== false,
    readingTime: a.readingTime ? String(a.readingTime) : '',
    relatedArticles: a.relatedArticles || [],
    mode: st === 'DRAFT' ? 'draft' : st === 'SCHEDULED' ? 'schedule' : 'publish',
    scheduledAt: st === 'SCHEDULED' && a.publishedAt ? a.publishedAt : '',
  };
};

/** فیلد متنی با برچسب و شمارنده */
const Field: React.FC<{ label: string; hint?: React.ReactNode; counter?: { value: number; min?: number; max: number }; children: React.ReactNode; required?: boolean }> = ({
  label,
  hint,
  counter,
  children,
  required,
}) => (
  <label className="block space-y-1.5">
    <span className="flex items-center justify-between text-sm font-bold text-gray-800">
      <span>
        {label}
        {required && <span className="mr-1 text-red-600">*</span>}
      </span>
      {counter && (
        <span
          className={`text-xs font-medium ${
            counter.value > counter.max || (counter.min && counter.value > 0 && counter.value < counter.min) ? 'text-amber-600' : 'text-gray-400'
          }`}
        >
          {faNumber(counter.value)} / {faNumber(counter.max)}
        </span>
      )}
    </span>
    {children}
    {hint && <span className="block text-xs leading-6 text-gray-500">{hint}</span>}
  </label>
);

const inputClass = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-violet-300 focus:outline-none focus:ring-2 focus:ring-violet-100';

/** آپلود تصویر با uploadsService و پیش‌نمایش */
const ImageUploadField: React.FC<{
  label: string;
  value: string;
  onChange: (key: string) => void;
  aspect?: string;
  hint?: string;
}> = ({ label, value, onChange, aspect = 'aspect-video', hint }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const preview = imagePreviewUrl(value);

  const upload = async (file: File) => {
    setError('');
    if (!/^image\/(jpeg|jpg|png|webp|gif)$/i.test(file.type)) {
      setError('فقط تصویر JPG، PNG، WebP یا GIF مجاز است');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('حجم تصویر نباید بیشتر از ۱۰ مگابایت باشد');
      return;
    }
    setUploading(true);
    try {
      const res = await uploadsService.uploadImage(file);
      const key = res?.original || res?.processed;
      if (!key) throw new Error('no key');
      onChange(key);
    } catch (err) {
      setError(apiErrorMessage(err, 'آپلود تصویر ناموفق بود'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <span className="text-sm font-bold text-gray-800">{label}</span>
      <div className={`relative w-full overflow-hidden rounded-xl border-2 border-dashed ${preview ? 'border-transparent' : 'border-gray-200'} bg-gray-50 ${aspect}`}>
        {preview ? (
          <>
            <img src={preview} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-x-2 bottom-2 flex justify-end gap-2">
              <button type="button" onClick={() => inputRef.current?.click()} className="rounded-lg bg-white/90 px-3 py-1.5 text-xs font-bold text-gray-800 shadow hover:bg-white">
                تغییر تصویر
              </button>
              <button type="button" onClick={() => onChange('')} className="rounded-lg bg-white/90 p-1.5 text-red-600 shadow hover:bg-white" aria-label="حذف تصویر">
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </>
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()} className="flex h-full w-full flex-col items-center justify-center gap-2 text-sm font-bold text-gray-500 hover:text-[#4a2e68]">
            {uploading ? <Loader2 className="h-6 w-6 animate-spin" aria-hidden /> : <ImagePlus className="h-6 w-6" aria-hidden />}
            {uploading ? 'در حال آپلود…' : 'انتخاب و آپلود تصویر'}
          </button>
        )}
        {uploading && preview && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70">
            <Loader2 className="h-6 w-6 animate-spin text-[#4a2e68]" aria-hidden />
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(file);
          e.target.value = '';
        }}
      />
      {error && <span className="block text-xs text-red-600">{error}</span>}
      {hint && <span className="block text-xs leading-6 text-gray-500">{hint}</span>}
    </div>
  );
};

/** ورودی برچسب‌ها با پیشنهاد از برچسب‌های موجود */
const TagInput: React.FC<{ value: string[]; onChange: (tags: string[]) => void; suggestions: ArticleTagUsage[]; placeholder?: string }> = ({ value, onChange, suggestions, placeholder }) => {
  const [draft, setDraft] = useState('');
  const add = (raw: string) => {
    const tag = raw.replace(/^#/, '').trim();
    if (!tag || value.includes(tag)) return;
    onChange([...value, tag]);
  };
  const matches = draft.trim()
    ? suggestions.filter((s) => s.tag.includes(draft.trim().replace(/^#/, '')) && !value.includes(s.tag)).slice(0, 6)
    : [];
  return (
    <div className="space-y-2">
      <div className="flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-2 py-1.5 focus-within:border-violet-300 focus-within:ring-2 focus-within:ring-violet-100">
        {value.map((tag) => (
          <span key={tag} className="inline-flex items-center gap-1 rounded-lg bg-violet-50 px-2 py-1 text-xs font-bold text-violet-800">
            {tag}
            <button type="button" onClick={() => onChange(value.filter((t) => t !== tag))} aria-label={`حذف ${tag}`} className="text-violet-500 hover:text-red-600">
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',' || e.key === '،') {
              e.preventDefault();
              add(draft);
              setDraft('');
            } else if (e.key === 'Backspace' && !draft && value.length) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={() => {
            if (draft.trim()) {
              add(draft);
              setDraft('');
            }
          }}
          placeholder={value.length ? '' : placeholder || 'برچسب را بنویسید و Enter بزنید'}
          className="min-w-[140px] flex-1 border-0 bg-transparent px-1 py-1 text-sm focus:outline-none"
        />
      </div>
      {matches.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {matches.map((s) => (
            <button
              key={s.tag}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                add(s.tag);
                setDraft('');
              }}
              className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:border-violet-200 hover:bg-violet-50"
            >
              {s.tag} <span className="text-gray-400">({faNumber(s.count)})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** انتخاب مقالات مرتبط (دستی) با جست‌وجو بین مقالات منتشرشده */
const RelatedPicker: React.FC<{ articleId?: string; value: string[]; onChange: (ids: string[]) => void }> = ({ articleId, value, onChange }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Article[]>([]);
  const [selectedItems, setSelectedItems] = useState<Article[]>([]);

  useEffect(() => {
    if (!value.length) {
      setSelectedItems([]);
      return;
    }
    Promise.all(value.map((id) => articlesService.getById(id).catch(() => null))).then((items) =>
      setSelectedItems(items.filter(Boolean) as Article[]),
    );
    // فقط در بارگذاری اولیه
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return undefined;
    }
    const t = window.setTimeout(() => {
      articlesService
        .getAll({ search: query.trim(), status: 'published', limit: 8 })
        .then((res: any) => setResults((res?.data || []).filter((a: Article) => a.id !== articleId && !value.includes(a.id))))
        .catch(() => setResults([]));
    }, 300);
    return () => window.clearTimeout(t);
  }, [query, articleId, value]);

  const add = (a: Article) => {
    if (value.length >= 3) return;
    onChange([...value, a.id]);
    setSelectedItems((prev) => [...prev, a]);
    setQuery('');
    setResults([]);
  };

  const remove = (id: string) => {
    onChange(value.filter((v) => v !== id));
    setSelectedItems((prev) => prev.filter((a) => a.id !== id));
  };

  return (
    <div className="space-y-2">
      {selectedItems.map((a, i) => (
        <div key={a.id} className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <span className="truncate font-bold text-gray-800">
            {faNumber(i + 1)}. {a.title}
          </span>
          <button type="button" onClick={() => remove(a.id)} className="text-red-600" aria-label="حذف">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ))}
      {value.length < 3 && (
        <div className="relative">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="جست‌وجوی مقالهٔ منتشرشده…" className={inputClass} />
          {results.length > 0 && (
            <div className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
              {results.map((a) => (
                <button key={a.id} type="button" onClick={() => add(a)} className="block w-full px-3 py-2 text-right text-sm hover:bg-violet-50">
                  {a.title}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <span className="block text-xs leading-6 text-gray-500">حداکثر ۳ مقاله. اگر خالی بماند، مقالات هم‌دسته و هم‌برچسب خودکار نمایش داده می‌شوند.</span>
    </div>
  );
};

/** فرم ایجاد/ویرایش مقالهٔ وبلاگ */
const ArticleEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [article, setArticle] = useState<Article | null>(null);
  const [categories, setCategories] = useState<ArticleCategoryAdmin[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<ArticleTagUsage[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [tab, setTab] = useState<Tab>('content');
  const [slugTouched, setSlugTouched] = useState(isEdit);
  const [dirty, setDirty] = useState(false);
  const [seoReport, setSeoReport] = useState<{ score: number; issues: string[]; suggestions: string[] } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  }, []);

  useEffect(() => {
    articlesService.getManageCategories().then(setCategories).catch(() => setCategories([]));
    articlesService.getTags().then(setTagSuggestions).catch(() => setTagSuggestions([]));
  }, []);

  useEffect(() => {
    if (!id) {
      setForm(emptyForm);
      setArticle(null);
      setSlugTouched(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    articlesService
      .getById(id)
      .then((a) => {
        setArticle(a);
        setForm(fromArticle(a));
        setSlugTouched(true);
      })
      .catch((err) => setError(apiErrorMessage(err, 'مقاله پیدا نشد')))
      .finally(() => setLoading(false));
  }, [id]);

  // هشدار خروج با تغییرات ذخیره‌نشده
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const onTitleChange = (title: string) => {
    setForm((prev) => ({ ...prev, title, slug: slugTouched ? prev.slug : slugify(title) }));
    setDirty(true);
  };

  const appendSnippet = (html: string) => update('content', `${form.content || ''}${html}`);

  const effectiveMetaTitle = form.metaTitle || form.title;
  const effectiveMetaDescription = form.metaDescription || form.excerpt;
  const status = article ? articleStatus(article) : null;

  const validate = (): string | null => {
    if (!form.title.trim()) return 'عنوان مقاله را وارد کنید.';
    if (!form.content.trim() || !form.content.replace(/<[^>]*>/g, '').trim()) return 'متن مقاله خالی است.';
    if (form.mode === 'schedule') {
      if (!form.scheduledAt) return 'زمان انتشار را انتخاب کنید.';
      if (new Date(form.scheduledAt).getTime() <= Date.now()) return 'زمان‌بندی باید در آینده باشد؛ برای انتشار فوری «انتشار» را انتخاب کنید.';
    }
    if (form.videoUrl && !/^https?:\/\//i.test(form.videoUrl.trim())) return 'لینک ویدیو باید با http یا https شروع شود.';
    return null;
  };

  const buildPayload = () => {
    const nullable = (v: string) => (v.trim() ? v.trim() : undefined);
    const clear = (v: string) => v.trim(); // رشتهٔ خالی = پاک‌کردن مقدار قبلی
    const reading = parseInt(form.readingTime, 10);
    return {
      title: form.title.trim(),
      slug: form.slug.trim() || undefined,
      content: form.content,
      excerpt: clear(form.excerpt),
      cardSummary: clear(form.cardSummary),
      kicker: clear(form.kicker),
      featuredImage: clear(form.featuredImage),
      featuredImageAlt: clear(form.featuredImageAlt),
      featuredImageCaption: clear(form.featuredImageCaption),
      categoryId: form.categoryId || null,
      tags: form.tags,
      author: clear(form.author) || DEFAULT_AUTHOR,
      authorRole: clear(form.authorRole),
      authorBio: clear(form.authorBio),
      authorAvatar: clear(form.authorAvatar),
      videoUrl: clear(form.videoUrl),
      videoPoster: clear(form.videoPoster),
      videoDuration: clockToSeconds(form.videoDuration) ?? undefined,
      videoCaption: clear(form.videoCaption),
      videoQuality: clear(form.videoQuality),
      quoteText: clear(form.quoteText),
      quoteAuthor: clear(form.quoteAuthor),
      metaTitle: clear(form.metaTitle),
      metaDescription: clear(form.metaDescription),
      focusKeyword: clear(form.focusKeyword),
      metaKeywords: form.metaKeywords,
      canonicalUrl: clear(form.canonicalUrl),
      ogTitle: clear(form.ogTitle),
      ogDescription: clear(form.ogDescription),
      ogImage: nullable(form.ogImage),
      isFeatured: form.isFeatured,
      allowComments: form.allowComments,
      readingTime: Number.isFinite(reading) && reading > 0 ? reading : undefined,
      relatedArticles: form.relatedArticles,
      published: form.mode !== 'draft',
      scheduledAt: form.mode === 'schedule' && form.scheduledAt ? new Date(form.scheduledAt).toISOString() : null,
    };
  };

  const save = async (opts: { preview?: boolean } = {}) => {
    const problem = validate();
    if (problem) {
      setError(problem);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setError('');
    setSaving(true);
    // پنجرهٔ پیش‌نمایش قبل از await باز می‌شود تا مرورگر آن را مسدود نکند
    const previewWindow = opts.preview ? window.open('about:blank', '_blank') : null;
    try {
      const payload = buildPayload();
      const saved = isEdit && id ? await articlesService.update(id, payload) : await articlesService.create(payload);
      setArticle(saved);
      setForm(fromArticle(saved));
      setDirty(false);
      showToast(isEdit ? 'تغییرات ذخیره شد' : 'مقاله ساخته شد');
      if (previewWindow) previewWindow.location.href = publicArticleUrl(saved, articleStatus(saved) !== 'PUBLISHED');
      if (!isEdit) navigate(`/articles/${saved.id}/edit`, { replace: true });
    } catch (err) {
      previewWindow?.close();
      setError(apiErrorMessage(err, 'ذخیرهٔ مقاله ناموفق بود'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const runSeoAnalysis = async () => {
    if (!id) return;
    try {
      setSeoReport(await articlesService.analyzeSeo(id));
    } catch (err) {
      showToast(apiErrorMessage(err, 'تحلیل سئو انجام نشد'));
    }
  };

  const doDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await articlesService.delete(id);
      setDirty(false);
      navigate('/articles', { replace: true });
    } catch (err) {
      showToast(apiErrorMessage(err, 'حذف انجام نشد'));
      setDeleting(false);
    }
  };

  const tabs: Array<{ key: Tab; label: string }> = useMemo(
    () => [
      { key: 'content', label: 'متن مقاله' },
      { key: 'media', label: 'تصویر، ویدیو و نویسنده' },
      { key: 'seo', label: 'سئو و اشتراک‌گذاری' },
      { key: 'settings', label: 'دسته، برچسب و مرتبط‌ها' },
    ],
    [],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <BrandSpinnerRing size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-24">
      <BlogSectionNav
        title={isEdit ? 'ویرایش مقاله' : 'نوشتن مقالهٔ جدید'}
        description={
          isEdit && article ? (
            <span className="inline-flex flex-wrap items-center gap-2">
              <span className={`rounded-lg px-2 py-0.5 text-xs font-black ring-1 ${STATUS_META[articleStatus(article)].className}`}>{STATUS_META[articleStatus(article)].label}</span>
              آخرین ویرایش: {faDateTime(article.updatedAt)} • بازدید: {faNumber(article.viewCount)} • مفید بود: {faNumber(article.helpfulYes)} / نبود: {faNumber(article.helpfulNo)}
            </span>
          ) : (
            'متن را بنویسید، تصویر شاخص بگذارید و در انتها انتشار یا زمان‌بندی را انتخاب کنید.'
          )
        }
        action={
          <Link to="/articles" className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50">
            <ArrowRight className="h-4 w-4" aria-hidden />
            بازگشت به فهرست
          </Link>
        }
      />

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-800">{error}</div>}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        {/* ستون اصلی */}
        <div className="space-y-5">
          <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <Field label="عنوان مقاله" required counter={{ value: form.title.length, min: 30, max: 70 }}>
              <input value={form.title} onChange={(e) => onTitleChange(e.target.value)} placeholder="مثلاً: چرا بعضی الگوها در زندگی ما تکرار می‌شوند؟" className={`${inputClass} text-base font-bold`} />
            </Field>
            <Field
              label="نامک (slug)"
              hint={
                <>
                  آدرس صفحه: <span dir="ltr" className="font-mono text-gray-700">{`${blogSiteBaseUrl()}/articles/${form.slug || '…'}`}</span>
                  {!slugTouched && ' — از روی عنوان ساخته می‌شود.'}
                </>
              }
            >
              <div className="flex gap-2">
                <input
                  value={form.slug}
                  dir="auto"
                  onChange={(e) => {
                    setSlugTouched(true);
                    update('slug', e.target.value);
                  }}
                  className={`${inputClass} font-mono`}
                />
                <button
                  type="button"
                  onClick={() => {
                    setSlugTouched(false);
                    update('slug', slugify(form.title));
                  }}
                  className="shrink-0 rounded-xl border border-gray-200 px-3 text-xs font-bold text-gray-700 hover:bg-gray-50"
                >
                  از عنوان
                </button>
              </div>
            </Field>
          </div>

          {/* تب‌ها */}
          <div className="flex gap-1 overflow-x-auto rounded-2xl border border-gray-200 bg-white p-1 shadow-sm">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`shrink-0 rounded-xl px-4 py-2 text-sm font-bold ${tab === t.key ? 'bg-violet-50 text-[#4a2e68] ring-1 ring-violet-200' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'content' && (
            <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <Field label="خلاصه / لید" counter={{ value: form.excerpt.length, min: 80, max: 200 }} hint="زیر عنوان صفحهٔ مقاله و روی کارت‌های «منتخب» نمایش داده می‌شود. خالی = ساخت خودکار از متن.">
                <textarea value={form.excerpt} onChange={(e) => update('excerpt', e.target.value)} rows={3} className={inputClass} />
              </Field>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
                <Field label="خلاصهٔ کارت (اختیاری)" counter={{ value: form.cardSummary.length, min: 60, max: 140 }} hint="متن کوتاه کارت در «همه مقالات» و «مقالات مرتبط». خالی = همان خلاصه/لید.">
                  <textarea value={form.cardSummary} onChange={(e) => update('cardSummary', e.target.value)} rows={2} maxLength={400} className={inputClass} />
                </Field>
                <Field label="روتیتر (اختیاری)" hint="برچسب کوتاه کنار دسته بالای عنوان در موبایل؛ مثل «تحلیل روان».">
                  <input value={form.kicker} onChange={(e) => update('kicker', e.target.value)} maxLength={60} className={inputClass} />
                </Field>
              </div>

              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-bold text-gray-800">
                    متن مقاله <span className="text-red-600">*</span>
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {CONTENT_SNIPPETS.map((s) => (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => appendSnippet(s.html)}
                        className="rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1 text-xs font-bold text-violet-800 hover:bg-violet-100"
                        title="به انتهای متن اضافه می‌شود؛ سپس متن نمونه را ویرایش کنید"
                      >
                        + {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <RichTextEditor value={form.content} onChange={(content) => update('content', content)} height={560} />
                <p className="text-xs leading-6 text-gray-500">
                  برای «فهرست مطالب» از سرفصل‌های «Heading 2» استفاده کنید. دکمه‌های بالا بلوک‌های طراحی‌شده (نقل‌قول، نکته کلیدی، گام، تمرین) را به انتهای متن اضافه می‌کنند. اگر
                  «جای ویدیو در متن» را نگذارید، ویدیو پیش از سرفصل سوم نمایش داده می‌شود.
                </p>
              </div>
            </div>
          )}

          {tab === 'media' && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm md:grid-cols-2">
                <ImageUploadField label="تصویر شاخص" value={form.featuredImage} onChange={(key) => update('featuredImage', key)} hint="نسبت ۱۶:۹، حداقل ۱۲۰۰×۶۷۵ پیکسل." />
                <div className="space-y-4">
                  <Field label="متن جایگزین تصویر (alt)" hint="برای دسترس‌پذیری و سئو؛ تصویر را توصیف کنید.">
                    <input value={form.featuredImageAlt} onChange={(e) => update('featuredImageAlt', e.target.value)} className={inputClass} />
                  </Field>
                  <Field label="کپشن زیر تصویر">
                    <textarea value={form.featuredImageCaption} onChange={(e) => update('featuredImageCaption', e.target.value)} rows={3} className={inputClass} />
                  </Field>
                </div>
              </div>

              <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="text-sm font-black text-gray-900">ویدیوی همراه مقاله (اختیاری)</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="space-y-4">
                    <Field label="لینک ویدیو" hint="لینک صفحهٔ آپارات (…aparat.com/v/…) یا آدرس مستقیم فایل mp4. خالی = مقاله بدون ویدیو.">
                      <input value={form.videoUrl} dir="ltr" onChange={(e) => update('videoUrl', e.target.value)} placeholder="https://www.aparat.com/v/…" className={inputClass} />
                    </Field>
                    <Field label="مدت ویدیو" hint="به شکل دقیقه:ثانیه، مثلاً ۴:۳۰">
                      <input value={form.videoDuration} dir="ltr" onChange={(e) => update('videoDuration', e.target.value)} placeholder="4:30" className={inputClass} />
                    </Field>
                    <Field label="توضیح زیر ویدیو">
                      <input value={form.videoCaption} onChange={(e) => update('videoCaption', e.target.value)} className={inputClass} />
                    </Field>
                    <Field label="برچسب کیفیت (موبایل)" hint="کنار عنوان ویدیو در موبایل؛ مثلاً «کیفیت HD». خالی = نمایش داده نمی‌شود.">
                      <input value={form.videoQuality} onChange={(e) => update('videoQuality', e.target.value)} maxLength={40} className={inputClass} />
                    </Field>
                  </div>
                  <ImageUploadField label="پوستر ویدیو" value={form.videoPoster} onChange={(key) => update('videoPoster', key)} hint="خالی = تصویر شاخص مقاله." />
                </div>
              </div>

              <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="text-sm font-black text-gray-900">نویسنده</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_180px]">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="نام نویسنده">
                        <input value={form.author} onChange={(e) => update('author', e.target.value)} className={inputClass} />
                      </Field>
                      <Field label="نقش / عنوان">
                        <input value={form.authorRole} onChange={(e) => update('authorRole', e.target.value)} className={inputClass} />
                      </Field>
                    </div>
                    <Field label="معرفی کوتاه" hint="در باکس نویسنده زیر مقاله نمایش داده می‌شود.">
                      <textarea value={form.authorBio} onChange={(e) => update('authorBio', e.target.value)} rows={3} className={inputClass} />
                    </Field>
                  </div>
                  <ImageUploadField label="عکس نویسنده" value={form.authorAvatar} onChange={(key) => update('authorAvatar', key)} aspect="aspect-square" hint="خالی = حروف اول نام." />
                </div>
              </div>

              <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="text-sm font-black text-gray-900">نقل‌قول کوتاه ستون کناری (دسکتاپ)</h3>
                <Field label="متن نقل‌قول">
                  <textarea value={form.quoteText} onChange={(e) => update('quoteText', e.target.value)} rows={2} placeholder="«آگاهی، نخستین قدم برای دگرگونی است…»" className={inputClass} />
                </Field>
                <Field label="گوینده">
                  <input value={form.quoteAuthor} onChange={(e) => update('quoteAuthor', e.target.value)} className={inputClass} />
                </Field>
              </div>
            </div>
          )}

          {tab === 'seo' && (
            <div className="space-y-5">
              <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <Field label="عنوان سئو (meta title)" counter={{ value: effectiveMetaTitle.length, min: 30, max: 60 }} hint="خالی = عنوان مقاله.">
                  <input value={form.metaTitle} onChange={(e) => update('metaTitle', e.target.value)} placeholder={form.title} className={inputClass} />
                </Field>
                <Field label="توضیحات متا" counter={{ value: effectiveMetaDescription.length, min: 120, max: 160 }} hint="خالی = خلاصهٔ مقاله.">
                  <textarea value={form.metaDescription} onChange={(e) => update('metaDescription', e.target.value)} rows={3} placeholder={form.excerpt} className={inputClass} />
                </Field>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="کلمهٔ کلیدی اصلی">
                    <input value={form.focusKeyword} onChange={(e) => update('focusKeyword', e.target.value)} className={inputClass} />
                  </Field>
                  <Field label="آدرس کانونیکال" hint="فقط اگر این مطلب نسخهٔ اصلی جای دیگری دارد.">
                    <input value={form.canonicalUrl} dir="ltr" onChange={(e) => update('canonicalUrl', e.target.value)} className={inputClass} />
                  </Field>
                </div>
                <div className="space-y-1.5">
                  <span className="text-sm font-bold text-gray-800">کلمات کلیدی متا</span>
                  <TagInput value={form.metaKeywords} onChange={(v) => update('metaKeywords', v)} suggestions={[]} placeholder="کلمه را بنویسید و Enter بزنید" />
                </div>
              </div>

              <SeoPreview title={effectiveMetaTitle} description={effectiveMetaDescription} slug={form.slug} baseUrl={`${blogSiteBaseUrl()}/articles/`} />

              <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <h3 className="text-sm font-black text-gray-900">اشتراک‌گذاری در شبکه‌های اجتماعی (Open Graph)</h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Field label="عنوان OG" hint="خالی = عنوان سئو.">
                    <input value={form.ogTitle} onChange={(e) => update('ogTitle', e.target.value)} className={inputClass} />
                  </Field>
                  <Field label="آدرس تصویر OG" hint="خالی = تصویر شاخص.">
                    <input value={form.ogImage} dir="ltr" onChange={(e) => update('ogImage', e.target.value)} className={inputClass} />
                  </Field>
                </div>
                <Field label="توضیحات OG" hint="خالی = توضیحات متا.">
                  <textarea value={form.ogDescription} onChange={(e) => update('ogDescription', e.target.value)} rows={2} className={inputClass} />
                </Field>
              </div>

              <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-black text-gray-900">تحلیل سئو</h3>
                  <button
                    type="button"
                    onClick={runSeoAnalysis}
                    disabled={!isEdit || dirty}
                    className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-bold text-violet-800 disabled:opacity-50"
                    title={!isEdit || dirty ? 'ابتدا مقاله را ذخیره کنید' : ''}
                  >
                    <Sparkles className="h-4 w-4" aria-hidden />
                    تحلیل نسخهٔ ذخیره‌شده
                  </button>
                </div>
                {seoReport ? (
                  <div className="space-y-2 text-sm">
                    <p className="font-black">
                      امتیاز: <span className={seoReport.score >= 80 ? 'text-emerald-700' : seoReport.score >= 60 ? 'text-amber-700' : 'text-red-700'}>{faNumber(seoReport.score)} از ۱۰۰</span>
                    </p>
                    {seoReport.issues.map((i) => (
                      <p key={i} className="text-red-700">
                        • {i}
                      </p>
                    ))}
                    {seoReport.suggestions.map((s) => (
                      <p key={s} className="text-amber-700">
                        • {s}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">پس از ذخیره، طول عنوان، توضیحات، کلمهٔ کلیدی، تصویر و حجم متن بررسی می‌شود.</p>
                )}
              </div>
            </div>
          )}

          {tab === 'settings' && (
            <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <Field
                label="دسته‌بندی"
                hint={
                  <>
                    دسته در چیپ‌های صفحهٔ وبلاگ و بج کارت‌ها دیده می‌شود. <Link to="/articles/categories" className="font-bold text-[#4a2e68] hover:underline">مدیریت دسته‌ها</Link>
                  </>
                }
              >
                <select value={form.categoryId} onChange={(e) => update('categoryId', e.target.value)} className={inputClass}>
                  <option value="">بدون دسته</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {!c.isActive ? ' (غیرفعال)' : ''}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="space-y-1.5">
                <span className="text-sm font-bold text-gray-800">برچسب‌ها</span>
                <TagInput value={form.tags} onChange={(v) => update('tags', v)} suggestions={tagSuggestions} />
                <span className="block text-xs text-gray-500">اولین برچسب در موبایل کنار دسته نمایش داده می‌شود.</span>
              </div>
              <div className="space-y-1.5">
                <span className="text-sm font-bold text-gray-800">مقالات مرتبط (دستی)</span>
                <RelatedPicker articleId={id} value={form.relatedArticles} onChange={(v) => update('relatedArticles', v)} />
              </div>
              <Field label="زمان مطالعه (دقیقه)" hint="خالی = محاسبهٔ خودکار از متن (۲۰۰ کلمه در دقیقه).">
                <input type="number" min={1} value={form.readingTime} onChange={(e) => update('readingTime', e.target.value)} className={`${inputClass} max-w-[160px]`} />
              </Field>
            </div>
          )}
        </div>

        {/* ستون کناری: انتشار */}
        <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
          <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-black text-gray-900">انتشار</h3>
            <div className="space-y-2">
              {(
                [
                  { key: 'draft', label: 'پیش‌نویس', hint: 'در سایت دیده نمی‌شود' },
                  { key: 'publish', label: 'انتشار', hint: status === 'PUBLISHED' ? 'منتشر است' : 'بلافاصله پس از ذخیره' },
                  { key: 'schedule', label: 'زمان‌بندی', hint: 'در زمان انتخابی منتشر می‌شود' },
                ] as Array<{ key: PublishMode; label: string; hint: string }>
              ).map((m) => (
                <label key={m.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${form.mode === m.key ? 'border-violet-300 bg-violet-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="publish-mode" checked={form.mode === m.key} onChange={() => update('mode', m.key)} className="mt-1 accent-[#4a2e68]" />
                  <span>
                    <span className="block text-sm font-bold text-gray-900">{m.label}</span>
                    <span className="block text-xs text-gray-500">{m.hint}</span>
                  </span>
                </label>
              ))}
            </div>
            {form.mode === 'schedule' && (
              <div className="space-y-1.5">
                <span className="text-sm font-bold text-gray-800">زمان انتشار</span>
                <PersianDatePicker value={form.scheduledAt} onChange={(v) => update('scheduledAt', v)} placeholder="تاریخ و ساعت انتشار" />
              </div>
            )}
            {article?.publishedAt && status === 'PUBLISHED' && <p className="text-xs text-gray-500">تاریخ انتشار: {faDateTime(article.publishedAt)}</p>}

            <label className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 p-3">
              <span className="inline-flex items-center gap-2 text-sm font-bold text-gray-800">
                <Star className={`h-4 w-4 ${form.isFeatured ? 'fill-amber-400 text-amber-500' : 'text-gray-400'}`} aria-hidden />
                «منتخب من حقیقی»
              </span>
              <input type="checkbox" checked={form.isFeatured} onChange={(e) => update('isFeatured', e.target.checked)} className="h-4 w-4 accent-[#4a2e68]" />
            </label>
            <label className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 p-3">
              <span className="text-sm font-bold text-gray-800">دیدگاه‌ها فعال باشد</span>
              <input type="checkbox" checked={form.allowComments} onChange={(e) => update('allowComments', e.target.checked)} className="h-4 w-4 accent-[#4a2e68]" />
            </label>

            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={() => save()}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#4a2e68] px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-[#392352] disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
                {form.mode === 'draft' ? 'ذخیرهٔ پیش‌نویس' : form.mode === 'schedule' ? 'ذخیره و زمان‌بندی' : 'ذخیره و انتشار'}
              </button>
              <button
                type="button"
                onClick={() => save({ preview: true })}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm font-bold text-violet-800 hover:bg-violet-100 disabled:opacity-60"
              >
                <Eye className="h-4 w-4" aria-hidden />
                ذخیره و پیش‌نمایش در سایت
              </button>
              {dirty && <p className="text-center text-xs font-bold text-amber-700">تغییرات ذخیره نشده‌اند</p>}
            </div>
          </div>

          {isEdit && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              حذف مقاله
            </button>
          )}
        </aside>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        danger
        busy={deleting}
        title="حذف مقاله"
        confirmLabel="حذف قطعی"
        message={<>مقالهٔ «{form.title}» برای همیشه حذف می‌شود. برای برداشتن موقت از سایت، آن را به پیش‌نویس برگردانید.</>}
        onConfirm={doDelete}
        onClose={() => setConfirmDelete(false)}
      />

      {toast && (
        <div role="status" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

export default ArticleEditor;
